// ARES API integrace pro automatické doplnění údajů firem

export const fetchCompanyFromARES = async (ico) => {
  // Odstranění mezer a neplatných znaků z IČO
  const cleanIco = ico.replace(/\s/g, '').replace(/\D/g, '');
  
  if (cleanIco.length !== 8) {
    throw new Error('IČO musí mít 8 číslic');
  }

  try {
    // ARES API endpoint - aktualizovaná verze
    const response = await fetch(
      `https://ares.gov.cz/ekonomicke-subjekty-v-be/rest/ekonomicke-subjekty/${cleanIco}`,
      {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        mode: 'cors'
      }
    );

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Firma s tímto IČO nebyla nalezena v ARES');
      }
      throw new Error(`ARES API chyba: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    
    if (!data) {
      throw new Error('Žádná data z ARES');
    }

    console.log('ARES data:', data); // Debug log
    
    // Extrakce údajů z ARES odpovědi - aktualizovaná struktura
    const company = data;
    const address = company.sidlo || company.adresaSidla || {};
    
    // Název firmy - zkusím různé možnosti
    const companyName = company.obchodniJmeno || 
                       company.nazevSubjektu || 
                       company.nazev || 
                       company.jmeno ||
                       (company.popis && company.popis.toString()) || 
                       '';
    
    // Rozlišení právnické a fyzické osoby
    const pravniForma = company.pravniForma?.nazev || company.pravniForma?.kod || '';
    let personType = '';
    
    console.log('ARES company object keys:', Object.keys(company)); // Debug
    console.log('pravniForma:', pravniForma); // Debug
    console.log('obchodniJmeno:', company.obchodniJmeno); // Debug
    console.log('jmeno:', company.jmeno); // Debug
    console.log('nazevSubjektu:', company.nazevSubjektu); // Debug
    
    // Určení typu osoby na základě právní formy nebo struktury dat
    // Nejprve zkontrolujeme právní formu, pokud existuje
    if (pravniForma) {
      // Fyzické osoby mají obvykle právní formy obsahující "fyzická"
      const pravniFormaLower = pravniForma.toLowerCase();
      if (pravniFormaLower.includes('fyzická') || 
          pravniFormaLower.includes('podnikající') ||
          pravniFormaLower.includes('živnost') ||
          pravniFormaLower.includes('osvc') ||
          pravniForma === '101' || // Kód pro fyzickou osobu
          pravniForma === '111' || // Kód pro fyzickou osobu - podnikatel
          pravniForma === '112') { // Kód pro zemědělský podnikatel
        personType = 'Fyzická osoba';
        if (pravniForma) {
          personType += ` (${pravniForma})`;
        }
      } else {
        personType = 'Právnická osoba';
        if (pravniForma) {
          personType += ` (${pravniForma})`;
        }
      }
    } else if (company.obchodniJmeno && !company.jmeno) {
      // Pokud má pouze obchodní jméno (bez jména), je to pravděpodobně PO
      personType = 'Právnická osoba';
    } else if (company.jmeno && !company.obchodniJmeno) {
      // Pokud má pouze jméno (bez obchodního jména), je to pravděpodobně FO
      personType = 'Fyzická osoba';
    } else if (company.nazevSubjektu) {
      // Fallback na název subjektu - pokud obsahuje typické zkratky pro PO
      const nazevLower = company.nazevSubjektu.toLowerCase();
      if (nazevLower.includes('s.r.o') || 
          nazevLower.includes('a.s.') || 
          nazevLower.includes('spol.') ||
          nazevLower.includes('družstvo') ||
          nazevLower.includes('o.p.s.') ||
          nazevLower.includes('s.p.')) {
        personType = 'Právnická osoba';
      } else {
        personType = 'Fyzická osoba';
      }
    } else {
      // Poslední fallback podle IČO - ale spíše označme jako neznámé
      personType = 'Neznámý typ';
    }
    
    // Podrobná adresa - zkusíme různé struktury
    let formattedAddress = '';
    if (address) {
      formattedAddress = formatAddress(address);
    }
    
    // Pokud nemáme adresu ze standardního pole, zkusme alternativní cesty
    if (!formattedAddress && company.adresa) {
      formattedAddress = formatAddress(company.adresa);
    }
    
    // Pokud stále nemáme adresu, zkusme textovou formu
    if (!formattedAddress && company.textovaAdresa) {
      formattedAddress = company.textovaAdresa;
    }
    
      // Zánik podle základního endpointu
      const reg = company.seznamRegistraci || {};
      const vrStav  = (reg.stavZdrojeVr  || '').toUpperCase();
      const rosStav = (reg.stavZdrojeRos || '').toUpperCase();
      const isInactive = !!company.datumZaniku
        || /zanik|vymaz|likvidac|zruš/i.test(vrStav)
        || /zanik|vymaz|likvidac|zruš/i.test(rosStav);

      // Pozastavení/přerušení živností – musíme se zeptat RZP endpointu
      let isSuspended = false;
      if (!isInactive) {
        try {
          const rzpRes = await fetch(
            `https://ares.gov.cz/ekonomicke-subjekty-v-be/rest/ekonomicke-subjekty-rzp/${cleanIco}`,
            { method: 'GET', headers: { 'Accept': 'application/json' } }
          );
          if (rzpRes.ok) {
            const rzpData = await rzpRes.json();
            const zaznam = (rzpData.zaznamy || [])[0] || {};
            const zs = zaznam.zivnostiStav || {};
            const aktivni   = zs.pocetAktivnich    ?? null;
            const prerusene = zs.pocetPrerusenych   ?? 0;
            const pozast    = zs.pocetPozastavenych ?? 0;
            const celkem    = zs.pocetCelkem        ?? 0;
            // Přerušené/pozastavené: žádná aktivní živnost a alespoň jedna přerušená/pozastavená
            if (celkem > 0 && aktivni === 0 && (prerusene > 0 || pozast > 0)) {
              isSuspended = true;
            }
          }
        } catch (_) { /* RZP nedostupné – ignoruj, status zůstane aktivní */ }
      }

      const stav = isInactive ? 'ZANIKLA' : isSuspended ? 'POZASTAVENO' : 'AKTIVNÍ';

      // Kraj (region) z adresy sídla
      const kraj = address.nazevKraje || address.kraj || '';

      return {
        name: companyName,
        ico: cleanIco,
        address: formattedAddress,
        kraj,
        personType: personType,
        dic: company.dic || '',
        pravniForma: pravniForma,
        datumVzniku: company.datumVzniku || '',
        datumZaniku: company.datumZaniku || '',
        cinnosti: company.cinnosti || [],
        stav,
      };
  } catch (error) {
    console.error('ARES API Error:', error);
    
    // Pokud je CORS problém, zkus alternativní přístup
    if (error.message.includes('CORS') || error.message.includes('fetch')) {
      throw new Error('Problém s připojením k ARES. Zkuste to prosím později nebo zadejte údaje ručně.');
    }
    
    throw new Error(`Chyba při načítání z ARES: ${error.message}`);
  }
};

// Pomocná funkce pro extrakci PSČ z adresy
const extractZipCode = (address) => {
  if (!address) return '';
  
  // Získáme PSČ z adresních údajů - zkusíme různé možnosti
  const zipCodeValue = address.psc || 
                      address.zip || 
                      address.zipCode || 
                      address.postalCode ||
                      address.postcode ||
                      address.pscObce ||
                      address.postCode;
  
  if (zipCodeValue) {
    const psc = zipCodeValue.toString().replace(/\s/g, '');
    // Formátujeme PSČ do správného tvaru XXX XX
    if (psc.length === 5) {
      const formatted = `${psc.slice(0, 3)} ${psc.slice(3)}`;
      return formatted;
    } else {
      return psc;
    }
  }
  
  return '';
};

// Pomocná funkce pro formátování adresy
const formatAddress = (address) => {
  if (!address) return '';

  // --- Ulice + číslo popisné/orientační ---
  // Formát: "Příčná 1892/4"  (bez mezery před lomítkem)
  let street = '';
  const ulice = address.nazevUlice || address.ulice || '';
  const cp    = address.cisloDomovni || address.cp || '';
  const co    = address.cisloOrientacni || address.co || '';

  if (ulice && cp && co) {
    street = `${ulice} ${cp}/${co}`;
  } else if (ulice && cp) {
    street = `${ulice} ${cp}`;
  } else if (ulice && co) {
    street = `${ulice} ${co}`;
  } else if (ulice) {
    street = ulice;
  } else if (cp && co) {
    street = `${cp}/${co}`;
  } else if (cp) {
    street = String(cp);
  }

  // --- Část obce (např. "Nové Město") ---
  const castObce = address.nazevCastiObce || address.castObce || '';

  // --- PSČ + město ---
  // Formát: "11000 Praha 1"  (PSČ před názvem)
  const zipCode = extractZipCode(address);
  // Pro Praha použijeme název městské části/obvodu (Praha 1, Praha 10 …)
  const obec    = address.nazevMestskeCastiObvodu || address.nazevObce || address.obec || '';
  let cityWithZip = '';
  if (zipCode && obec) {
    cityWithZip = `${zipCode} ${obec}`;
  } else if (obec) {
    cityWithZip = obec;
  } else if (zipCode) {
    cityWithZip = zipCode;
  }

  // Pokud nemáme žádné části, zkusme textovou adresu
  if (!street && !castObce && !cityWithZip && address.textovaAdresa) {
    return address.textovaAdresa;
  }

  // Složíme části oddělené čárkou (vynecháme prázdné)
  return [street, castObce, cityWithZip].filter(Boolean).join(', ');
};

// Validace IČO podle kontrolního součtu - opravený algoritmus
export const validateIco = (ico) => {
  const cleanIco = ico.replace(/\s/g, '').replace(/\D/g, '');
  
  // IČO musí mít přesně 8 číslic
  if (cleanIco.length !== 8) {
    return false;
  }
  
  // Některá speciální IČO jsou vždy platná (např. státní organizace)
  const specialIcos = ['00000000', '11111111', '22222222', '33333333', '44444444', '55555555', '66666666', '77777777', '88888888', '99999999'];
  if (specialIcos.includes(cleanIco)) {
    return true;
  }
  
  const digits = cleanIco.split('').map(Number);
  const weights = [8, 7, 6, 5, 4, 3, 2];
  
  let sum = 0;
  for (let i = 0; i < 7; i++) {
    sum += digits[i] * weights[i];
  }
  
  const remainder = sum % 11;
  
  // Výpočet kontrolní číslice
  let expectedCheckDigit;
  if (remainder === 0) {
    expectedCheckDigit = 1;
  } else if (remainder === 1) {
    expectedCheckDigit = 0;
  } else {
    expectedCheckDigit = 11 - remainder;
  }
  
  // Pro některá IČO je kontrolní součet vypočítán jinak
  if (expectedCheckDigit === 10) {
    return false; // IČO s kontrolní číslicí 10 neexistuje
  }
  
  return expectedCheckDigit === digits[7];
};

// Kategorizace stavu IČO – odolná vůči velikosti písmen i různým variantám názvu
// (např. "aktivní"/"AKTIVNÍ", "pozastavené"/"POZASTAVENO", "neaktivní"/"ZANIKLA").
// Vrací: 'active' | 'suspended' | 'inactive' | '' (prázdné).
export const getIcoStatusCategory = (status) => {
  if (!status) return '';
  const s = String(status).toLowerCase();
  if (s.includes('pozast') || s.includes('přeruš') || s.includes('preruš')) return 'suspended';
  if (s.includes('neaktiv') || s.includes('zanik') || s.includes('vymaz') || s.includes('likvidac') || s.includes('zruš')) return 'inactive';
  if (s.includes('aktiv')) return 'active';
  return 'inactive';
};
